import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadYaml } from '../utilis/loadYAML';
import './Home.css';
import BifurcatedPromo from '../components/BifurcatedPromo';

interface Section {
  id?: string;
  heading?: string;
  images?: string[];
  type: string;
  title?: string;
  description?: string;
  video?: string;
  caption?: string;
  linkText?: string;
  linkUrl?: string;
  linkHoverLine?: boolean;
  options?: {
    title: string;
    video: string;
  }[];
}

function Home() {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeVideo, setActiveVideo] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    loadYaml('/config/home.yaml').then((data: any) => {
      setSections(data.sections);
      const switcherSection = data.sections.find((s: Section) => s.type === 'videoSwitcher');
      if (switcherSection && switcherSection.options?.[0]) {
        setActiveVideo(switcherSection.options[0].video);
      }
    });
  }, []);

  return (
    <div className="homepage-container">
      {sections.map((section, idx) => {
        const sectionContent = (() => {
          // === VIDEO ===
          if (section.type === 'video' && section.video) {
            return (
              <section className="section video-section" id={section.id || ''} key={idx}>
                <video autoPlay muted loop>
                  <source src={section.video} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {section.caption && <div className="video-caption">{section.caption}</div>}
              </section>
            );
          }

          // === TEXT ===
          if (section.type === 'text') {
            const [boldLine, ...rest] = (section.description || '').split('---');
            const secondHalf = rest.join('---').trim();

            return (
              <section
                className={`section text-section ${section.id || ''}`}
                id={section.id || ''}
                key={idx}
              >
                <h2>{section.title}</h2>
                <div className="text-bifurcated-container">
                  <div className="text-bifurcated-left">
                    {boldLine && <p className="bold-line">{boldLine.trim()}</p>}
                  </div>
                  <div className="text-bifurcated-right">
                    {secondHalf && <p className="secondary-text">{secondHalf}</p>}
                    {section.linkText && section.linkUrl && (
                      <div
                        className={`mission-link ${section.linkHoverLine ? 'hover-line' : ''}`}
                        onClick={() => navigate(section.linkUrl!)}
                      >
                        {section.linkText}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            );
          }

          // === VIDEO SWITCHER ===
          if (section.type === 'videoSwitcher') {
            return (
              <section className="section video-switcher-section" id={section.id || ''} key={idx}>
                <h2>{section.title}</h2>
                <div className="switcher-container">
                  <div className="switcher-video">
                    <video key={activeVideo} controls autoPlay muted>
                      <source src={activeVideo} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="switcher-options">
                    {section.options?.map((opt, i) => (
                      <div
                        key={i}
                        className={`switcher-box ${activeVideo === opt.video ? 'active' : ''}`}
                        onClick={() => setActiveVideo(opt.video)}
                      >
                        {opt.title}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          }

          // === BIFURCATED PROMO ===
          if (section.type === 'bifurcatedPromo') {
            return (
              <BifurcatedPromo
                key={idx}
                id={section.id ?? ''}
                title={section.title ?? ''}
                heading={section.heading ?? ''}
                description={section.description ?? ''}
                images={section.images ?? []}
              />
            );
          }

          return null;
        })();

        const nextSection = sections[idx + 1];
        const showDivider =
          section.type !== 'video' &&
          nextSection &&
          nextSection.type !== 'video';

        return (
          <React.Fragment key={idx}>
            {sectionContent}
            {showDivider && <div className="section-divider" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default Home;
